import { faCircleXmark, faLock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog, Transition } from '@headlessui/react'
import { Link } from 'react-router-dom'
import { Fragment, useEffect, useMemo, useState } from 'react'

import useAuth from '../hooks/useAuth'
import useAxiosPrivate from '../hooks/useAxiosPrivate'

const ViewServiceSlideout = ({ open, setOpen, service }) => {
  const { auth, userPolicy, createInfo } = useAuth()
  const axiosPrivate = useAxiosPrivate()
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [accessMode, setAccessMode] = useState('fallback') // 'api' | 'fallback'
  const [accessByProduct, setAccessByProduct] = useState({})

  // Check if user profile is complete
  const isProfileComplete = () => {
    return auth?.user?.onboarding_complete === 1 ? true : false
  }

  // Check overall service access based on user policy
  const checkOverallServiceAccess = () => {
    return !(
      ['SWB OH+', 'SWB OH+ Only'].includes(userPolicy?.product) ||
      !['live', 'expiring'].includes(userPolicy?.status)
    )
  }

  // Check access for specific products
  const checkProductAccess = (productTitle) => {
    if (accessMode === 'api') {
      return !!accessByProduct?.[productTitle]
    }

    // First check overall access
    if (!checkOverallServiceAccess()) return false

    if (['SAS', 'Mutual'].includes(userPolicy?.department)) return true
    if (['SWB OH+', 'SWB OH+ Only'].includes(userPolicy?.department)) return false

    if (![
      'Counselling',
      'Mindfulness',
      'Relaxation Room Live',
      'Nurse Support',
      'Menopause Support',
      'GP Services',
      'SAS Gym',
      'Happier Healthier You',
      'HHY Community',
      'Physiotherapy',
      'Preparing Parents',
      'Food Intolerance Testing',
      'Financial Wellbeing',
      'Manspace',
      'Pupil Yoga',
      'Pastoral Resources',
      'Awards & Programmes',
      'Ask the PT'
    ].includes(productTitle)) return false

    switch (productTitle) {
      case 'Counselling':
        if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
        return true
      case 'GP Services':
        if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
        return true
      case 'Happier Healthier You':
        if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
        return true
      case 'Physiotherapy':
        if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
        return true
      case 'Preparing Parents':
        if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
        return true
      case 'Pupil Counselling':
        if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
        return true
      default:
        return true
    }
  }

  // Process payment - placeholder function
  const processPayment = (product) => {
    if (!!product?.paymentLink) {
      window.location.href = product.paymentLink;
    } else {
      createInfo('error', 'Payment link not available.')
    }
  }

  const borderColour = service?.colour || '#FDD2B7'
  const iconColor = service?.iconColour || '#FDD2B7'
  const title = service?.name || 'Service Name'
  const description = service?.description || 'Get faster support from clinicians for minor illness or non-urgent conditions.'
  const products = service?.products || []

  const productTitles = useMemo(
    () => products.map((p) => p?.title).filter(Boolean),
    [products]
  )

  useEffect(() => {
    if (!open) return
    if (!auth?.token) return
    if (productTitles.length === 0) return

    let cancelled = false
    setAccessMode('fallback')

    axiosPrivate
      .post('/service-access/check', {
        product_titles: productTitles,
        ...(process.env.REACT_APP_ENV !== 'production'
          ? {
              policy_override: {
                product: userPolicy?.product,
                status: userPolicy?.status,
                department: userPolicy?.department,
              },
            }
          : {}),
      })
      .then((response) => {
        if (cancelled) return
        const byProduct = response?.data?.result?.by_product
        if (byProduct && typeof byProduct === 'object') {
          setAccessByProduct(byProduct)
          setAccessMode('api')
        }
      })
      .catch(() => {
        if (cancelled) return
        setAccessMode('fallback')
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, auth?.token, userPolicy?.id, productTitles.join('|')])

  const handleProductClick = (product) => {
    // Check if profile is complete first
    if (!isProfileComplete()) {
      alert('Please complete your profile to access this service.')
      return
    }

      if(product.external) {
        window.open(product.link, '_blank')
        return;
      }
      // Navigate to the original link
      if (product.bookable) {
        window.location.href = "/book-assessment"
      } else {
        window.location.href = `/book-service/${product.link}`
      }
  }

  const PaymentModal = () => {
    return (
      <div className="text-center py-4">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Access to "{selectedProduct?.title}" Required
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          You don't have access to this service right now. Please upgrade your plan to access "{selectedProduct?.title}".
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => setPaymentModalOpen(false)}
            className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              processPayment(selectedProduct)
              setPaymentModalOpen(false)
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-md transition flex items-center justify-center gap-3"
          >
            {/* Stripe Logo */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="white" className="w-10 h-10 flex-shrink-0">
              <path d="M524.4 284.8C515.5 284.8 505.7 291.5 505.7 307.5L542.4 307.5C542.4 291.5 533.1 284.8 524.4 284.8zM407 287.4C398.8 287.4 393.7 290.3 390 294.4L390.2 347.2C393.7 350.9 398.7 353.9 407 353.9C420.1 353.9 428.9 339.6 428.9 320.5C428.9 301.9 419.9 287.3 407 287.4zM560 96L80 96C53.5 96 32 117.5 32 144L32 496C32 522.5 53.5 544 80 544L560 544C586.5 544 608 522.5 608 496L608 144C608 117.5 586.5 96 560 96zM154.2 345.1C154.2 370.7 133.9 385.2 104.3 385.4C92.1 385.4 78.7 383 65.5 377.3L65.5 343.4C77.5 349.8 92.6 354.7 104.4 354.7C112.3 354.7 118 352.6 118 346C118 329 64 335.4 64 296.1C64 270.9 83.2 255.9 112 255.9C123.8 255.9 135.5 257.7 147.3 262.4L147.3 295.8C136.5 290 122.8 286.7 112 286.7C104.5 286.7 99.9 288.9 99.9 294.4C99.9 310.4 154.2 302.8 154.2 345.1zM223 288.5L196 288.5L196 339C196 359.9 218.5 353.4 223 351.6L223 380.5C218.3 383.1 209.7 385.2 198.1 385.2C177 385.2 161.2 369.7 161.2 348.7L161.4 234.8L196.1 227.4L196.1 258.2L223 258.2L223 288.5zM297 290.9C292.5 289.4 278.3 287.3 269.9 298.3L269.9 382.7L234.4 382.7L234.4 258.2L265.1 258.2L267.3 268.7C275.6 253.4 292.2 256.5 296.9 258.2L297 258.2L297 290.9zM341.1 382.7L305.4 382.7L305.4 258.2L341.1 258.2L341.1 382.7zM341.1 239.8L305.4 247.4L305.4 218.5L341.1 210.9L341.1 239.8zM415.2 385.3C402.8 385.3 395.2 380 390.1 376.3L390 416.5L354.5 424L354.5 258.2L385.8 258.2L387.6 267C392.5 262.5 401.5 255.9 415.4 255.9C440.3 255.9 463.8 278.4 463.8 319.7C463.8 364.8 440.6 385.2 415.2 385.3zM575.6 333.8L506.1 333.8C507.7 350.4 519.9 355.3 533.7 355.3C547.8 355.3 558.9 352.3 568.6 347.4L568.6 376C558.9 381.3 546.2 385.2 529.2 385.2C494.6 385.2 470.4 363.5 470.4 320.7C470.4 284.5 490.9 255.8 524.7 255.8C558.4 255.8 576 284.5 576 320.9C576 324.4 575.7 331.8 575.6 333.8z" />
            </svg>
            <span>Pay to Access</span>
          </button>
        </div>
      </div>
    )
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={() => { setOpen(false); setPaymentModalOpen(false); }}>
        <Transition.Child
          as="div"
          className="fixed inset-0 bg-gray-500 bg-opacity-30 backdrop-blur-[3px]"
          enter="transition-opacity ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in-out duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-3xl pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-3xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-[#f8f6fb] shadow-xl relative">
                    <div className=" px-4 py-6 sm:px-6">
                      <div className="flex items-start justify-between">
                        {/* Header Section */}
                        <div className="flex items-start gap-4">
                          <div className={`${iconColor} px-4 py-3 rounded-xl`}>
                            <FontAwesomeIcon
                              icon={service?.icon}
                              className="text-[#41264B] text-xl"
                            />
                          </div>
                          <div>
                            <h2 className="text-2xl font-semibold text-[#41264B]">
                              {title} Services
                            </h2>
                            <p className="text-sm text-gray-600">
                              {description}
                            </p>
                          </div>
                        </div>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="relative  text-[#41264B] hover:text-nav-dark focus:outline-none focus:ring-2 focus:ring-nav"
                            onClick={() => { setOpen(false); setPaymentModalOpen(false); }}
                          >
                            <span className="sr-only">Close panel</span>
                            <FontAwesomeIcon
                              icon={faCircleXmark}
                              className="text-xl"
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    <section className="p-6 flex flex-col gap-6 h-full">
                      {products?.map((product, index) => {
                        return (
                          <div
                            className="bg-white rounded-xl shadow-md p-5 relative group"
                            key={index}
                          >
                            <h3
                              className={`${service?.textColour} font-semibold text-lg mb-2`}
                            >
                              {product.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                              {product.description}
                            </p>
                            {/* Check if user has access to show appropriate button */}
                            {isProfileComplete() ? (
                              <button
                                onClick={() => handleProductClick(product)}
                                className={`${iconColor} border-2 ${borderColour} text-[#41264B] font-medium px-5 py-1.5 rounded-md hover:bg-white transition`}
                              >
                                {product.bookable ? 'Book Assessment' : 'Learn More'}
                              </button>
                            ) : (
                              <button
                                className={`${iconColor} border-2 ${borderColour} text-[#41264B] font-medium px-5 py-1.5 rounded-md transition cursor-not-allowed opacity-75 flex items-center gap-2`}
                                disabled
                              >
                                <FontAwesomeIcon icon={faLock} className="text-sm" />
                                {product.bookable ? 'Book Assessment' : 'Learn More'}
                              </button>
                            )}

                            {/* Full card overlay for restricted access */}
                            {!(isProfileComplete()) && (
                              <div className="absolute inset-0 bg-gray-900 bg-opacity-90 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                                <div className="text-white text-center p-6">
                                  <FontAwesomeIcon icon={faLock} className="text-3xl mb-4" />
                                  <p className="text-sm leading-relaxed">
                                    {!isProfileComplete()
                                      && 'Please complete your profile to access this service'
                                    }
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </section>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default ViewServiceSlideout
